import React, { useId } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { teacherTypography } from '../../design/typography';
import { unifiedUI } from '../../design/unified';
import { developmentLevelLabel } from '../../shared/developmentLevels';
import DevelopmentLevelSelector from './DevelopmentLevelSelector';
import StudentResultAvatar from './StudentResultAvatar';

const tones = {
  pending: { label: 'Bekliyor', color: unifiedUI.secondary, bg: '#F0F2F7', Icon: ScheduleIcon },
  completed: { label: 'Tamamlandı', color: '#176846', bg: '#EDF9F2', Icon: CheckCircleOutlineIcon },
  draft: { label: 'Kaydedilmedi', color: '#825020', bg: '#FFF4E7', Icon: EditOutlinedIcon },
  absent: { label: 'Katılmadı', color: '#825020', bg: '#FFF4E7', Icon: PersonOffOutlinedIcon },
  error: { label: 'Hatalı giriş', color: '#B42318', bg: '#FFF0EE', Icon: ErrorOutlineIcon },
};

/** Controlled view only: persistence, validation, calculation and row order stay in the manager. */
export default function StudentResultEntryCard({ student, scoringMode, value, isAbsent = false, isDirty = false,
  savedSection = 'pending', hasValidResult = false, validationError = '', totalQuestions, estimatedScore = null,
  disabled = false, onValueChange, onAbsentToggle, onKeyDown, inputRef }) {
  const identityId = useId(), feedbackId = useId(), inputId = useId();
  const studentName = student.student_name || 'Öğrenci';
  const isDevelopment = scoringMode === 'development', isTest = scoringMode === 'test';
  const statusKey = validationError ? 'error' : isDirty ? 'draft' : isAbsent ? 'absent'
    : savedSection === 'completed' && hasValidResult ? 'completed' : 'pending';
  const { label, color, bg, Icon } = tones[statusKey];
  let feedback;
  if (validationError) feedback = validationError;
  else if (isAbsent) feedback = 'Öğrenci değerlendirmeye katılmadı.';
  else if (isDirty && savedSection === 'absent' && !hasValidResult) feedback = 'Katılmadı işareti kaldırıldı.';
  else if (isDevelopment) {
    const level = developmentLevelLabel(value);
    feedback = level ? `${level} · ${isDirty ? 'Kayda hazır.' : 'Kayıtlı sonuç.'}` : 'Gelişim düzeyi seçin.';
  } else if (isTest) feedback = hasValidResult && estimatedScore !== null
    ? `Otomatik puan: ${estimatedScore}/100` : `${totalQuestions || 0} soruda doğru sayısını girin.`;
  else feedback = hasValidResult ? isDirty ? 'Puan kayda hazır.' : '100 üzerinden puan · Kayıtlı sonuç.' : '0–100 arası puan girin.';

  const absentAction = <Button variant="outlined" disabled={disabled} disableRipple
    aria-label={`${studentName}: Katılmadı`} aria-pressed={isAbsent}
    aria-description={isAbsent ? 'Katılmadı işaretini kaldırır.' : 'Öğrenciyi katılmadı olarak işaretler.'}
    onClick={onAbsentToggle} sx={{ minHeight: 44, minWidth: 96, textTransform: 'none',
      alignSelf: 'end', justifySelf: 'end', fontWeight: 600, flexShrink: 0,
      color: isAbsent ? '#825020' : unifiedUI.secondary, bgcolor: isAbsent ? '#FFF4E7' : 'transparent',
      borderColor: isAbsent ? '#A96E2C' : '#7D8BA1',
      '&:hover': { borderColor: unifiedUI.purple, bgcolor: unifiedUI.purpleSurface },
      '&:focus-visible': { outline: `3px solid ${unifiedUI.purple}`, outlineOffset: 2 } }}>
    {isAbsent ? 'Geri al' : 'Girmedi'}
  </Button>;

  const actionRail = clearAction => <Box data-testid="student-result-entry-actions" sx={{
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 0.75, mt: 1,
    '& > :last-child': { ml: 'auto' } }}>
    {clearAction}{absentAction}
  </Box>;

  return <Paper component="article" aria-labelledby={identityId} data-testid="student-result-entry-card"
    data-student-id={student.student_id} data-entry-status={statusKey} elevation={0}
    sx={{ ...unifiedUI.surfaceCard, p: { xs: 1.5, sm: 2 },
      containerType: 'inline-size', containerName: 'student-result-entry',
      borderColor: validationError ? '#E6A6A0' : isDirty ? '#C7B5EC' : unifiedUI.border,
      '&:focus-within': { borderColor: unifiedUI.purple, boxShadow: '0 0 0 1px #6546BF' } }}>
    <Box data-testid="student-result-entry-header" sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)',
      gap: '6px 8px', alignItems: 'start',
      '@container student-result-entry (min-width:300px)': { gridTemplateColumns: 'minmax(0,1fr) auto' } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '48px minmax(0,1fr)', gap: 1, minWidth: 0 }}>
        <StudentResultAvatar studentId={student.student_id} studentName={studentName} />
        <Box sx={{ minWidth: 0 }}>
        <Typography id={identityId} component="h3" data-testid="course-assessment-student" sx={teacherTypography.studentName}>{studentName}</Typography>
        <Typography sx={{ ...teacherTypography.metadata, mt: 0.25, overflowWrap: 'anywhere' }}>
          #{student.student_no ?? student.student_id} · {student.class_name || 'Şube bilgisi yok'}
        </Typography>
        </Box>
      </Box>
      <Chip label={label} icon={<Icon aria-hidden="true" />} size="small" sx={{ gridColumn: 1, gridRow: 2, justifySelf: 'end',
        height: 'auto', minHeight: 28, color, bgcolor: bg, fontSize: 13, fontWeight: 600,
        '& .MuiChip-icon': { color, fontSize: 17 }, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
        '@container student-result-entry (min-width:300px)': { gridColumn: 2, gridRow: 1 } }} />
    </Box>

    <Box data-testid="student-result-entry-control" sx={{ mt: 1.5 }}>
      <Typography component={isDevelopment ? 'div' : 'label'} htmlFor={isDevelopment ? undefined : inputId}
        sx={{ ...teacherTypography.metricLabel, display: 'block', fontWeight: 600, mb: 0.75 }}>
        {isDevelopment ? 'Gelişim düzeyi' : isTest ? 'Doğru' : 'Puan'}
      </Typography>
      {isDevelopment ? <DevelopmentLevelSelector expanded studentName={studentName}
        value={isAbsent ? '' : value} disabled={isAbsent || disabled} onChange={onValueChange}
        renderActions={actionRail} describedBy={feedbackId} /> : <>
        <TextField id={inputId} type="text" size="small" fullWidth
          value={isAbsent ? '' : value} disabled={isAbsent || disabled} error={Boolean(validationError)}
          placeholder={isAbsent ? '' : isTest ? 'Örn. 18' : 'Örn. 85'}
          inputProps={{ 'aria-label': isTest ? 'Doğru sayısı' : '100 üzerinden puan', 'aria-describedby': feedbackId,
            inputMode: 'numeric', pattern: '[0-9]*', min: 0, max: isTest ? Number(totalQuestions) || undefined : 100 }}
          InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={teacherTypography.metadata}>
            / {isTest ? totalQuestions || '—' : 100}
          </Typography></InputAdornment> }}
          onChange={event => { if (!isAbsent && !disabled) onValueChange(event.target.value.replace(/[^0-9]/g, '')); }}
          onKeyDown={onKeyDown} inputRef={inputRef}
          sx={{ '& input': { minHeight: 44, boxSizing: 'border-box', fontWeight: 600, fontSize: 18 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#7D8BA1' },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: unifiedUI.purple },
            '& .MuiInputLabel-root.Mui-focused': { color: unifiedUI.purple },
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#B42318' },
            '& .MuiInputLabel-root.Mui-error': { color: '#B42318' },
            '& .MuiOutlinedInput-root.Mui-disabled': { bgcolor: '#F5F6FA' } }} />
        {actionRail()}
      </>}
    </Box>

    <Box data-testid="student-result-entry-footer" sx={{ mt: 0.75 }}>
      <Typography id={feedbackId} sx={{ ...teacherTypography.supportingText, fontSize: 13,
        color: validationError ? '#B42318' : unifiedUI.secondary, overflowWrap: 'anywhere' }}>{feedback}</Typography>
      {isDirty && <Typography sx={{ ...teacherTypography.metadata, color: '#825020', mt: 0.25 }}>
        Değişiklikler henüz kaydedilmedi.
      </Typography>}
    </Box>
  </Paper>;
}
